import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box, Typography, Chip, Divider, Stack,
  CircularProgress, Backdrop, IconButton, Tooltip,
  Accordion, AccordionSummary, AccordionDetails,
  ButtonBase, Skeleton,
} from "@mui/material";
import { Check, Info, ChartNoAxesCombined, Heart, Brain, Flame, Eye, Plus, Minus, FileClock } from "lucide-react";
import { useTheme, alpha } from "@mui/material/styles";
import PageProtectedLayout from "../../../../../components/Page/PageProtectedLayout.jsx";
import { useTranslation } from '../../../../../language';
import { API_URL } from "../../../../../constants";
import CustomDialogSmall from "../../../../../components/Dialog/DiaCompSmall";
import CustomDialog from "../../../../../components/Dialog/DiaCompMedium";
import TextInfoComp from "../../ReuseComps/TextInfoComp";
import MarkerTrendChart from "../../ReuseComps/MarkerTrendChart";
import OmegaCompositionChart from "../../ReuseComps/OmegaCompositionChart";
import { DateSelector } from "../../../Cockpit/HealthCockpit";

// marker_healthsubsystem → display order, mirrors the "Omega Health" grouping used in BloodtestsPanel
const OMEGA_SUBSYSTEM_ORDER = [
  "Total fatty acids",
  "Polyunsaturated Fatty Acids (PUFA)",
  "Omega-3 fatty acids",
  "Omega-6 fatty acids",
  "Monounsaturated Fatty Acids",
  "Saturated Fatty Acids",
];

const SUBSYSTEM_KEY = {
  "Total fatty acids":                  "subsystemFattyTotal",
  "Polyunsaturated Fatty Acids (PUFA)": "subsystemPUFA",
  "Omega-3 fatty acids":                "subsystemOmega3",
  "Omega-6 fatty acids":                "subsystemOmega6",
  "Monounsaturated Fatty Acids":        "subsystemMUFA",
  "Saturated Fatty Acids":              "subsystemSFA",
};

// Body-function sections → the OmegaInsight `sections` key (and legacy flat field of the same name)
const FUNCTION_TILES = [
  { key: "heart",        icon: Heart, titleKey: "omega_function_heart",        field: "heart_function" },
  { key: "brain",        icon: Brain, titleKey: "omega_function_brain",        field: "brain_function" },
  { key: "inflammation", icon: Flame, titleKey: "omega_function_inflammation", field: "inflammation_function" },
  { key: "eyes",         icon: Eye,   titleKey: "omega_function_eyes",        field: "eyes_function" },
];

// Small marker cards under the hero; labels are lab abbreviations, so not translated
const KEY_CARDS = [
  { code: "EPA",     label: "EPA" },
  { code: "DHA",     label: "DHA" },
  { code: "ALA",     label: "ALA" },
  { code: "RATIO63", label: "Omega-6 : Omega-3" },
];

const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)";

function statusColor(theme, status) {
  if (!status) return theme.palette.grey[400];
  if (status.toUpperCase() === "NORMAL") return theme.palette.traffic.green;
  return theme.palette.traffic.red;
}

function statusLabel(t, status) {
  const key = `bt_status_${(status || "").toLowerCase().replace(/\s+/g, "_")}`;
  return t(key) !== key ? t(key) : status;
}

const toNum = (v) => (v == null || v === "" || isNaN(parseFloat(v)) ? null : parseFloat(v));

// Where a value sits relative to the lab range and (if given) the optimal range
function rangeState(m) {
  const v = toNum(m?.value);
  if (v == null) return null;
  const lo = toNum(m.ref_low), hi = toNum(m.ref_high);
  const oLo = toNum(m.opt_low), oHi = toNum(m.opt_high);
  if (lo != null && v < lo) return "below";
  if (hi != null && v > hi) return "above";
  if (oLo == null && oHi == null) return "normal";
  if (oLo != null && v < oLo) return "below_opt";
  if (oHi != null && v > oHi) return "above_opt";
  return "optimal";
}

const isOutOfRange = (state) => state === "below" || state === "above";

// Percent positions for a range track; one-sided ranges run to the track edge
function rangeGeometry(m) {
  const v = toNum(m?.value);
  const lo = toNum(m?.ref_low), hi = toNum(m?.ref_high);
  const oLo = toNum(m?.opt_low), oHi = toNum(m?.opt_high);
  const nums = [v, lo, hi, oLo, oHi].filter((n) => n != null);
  if (v == null || nums.length < 2) return null;
  const min = Math.min(...nums), max = Math.max(...nums);
  const span = max - min || Math.abs(max) || 1;
  let dMin = min - span * 0.9;
  const dMax = max + span * 0.35;
  if (min >= 0) dMin = Math.max(0, dMin);
  const pct = (n) => Math.max(0, Math.min(100, ((n - dMin) / (dMax - dMin)) * 100));
  const hasRef = lo != null || hi != null;
  const hasOpt = oLo != null || oHi != null;
  return {
    val: pct(v),
    refL: lo != null ? pct(lo) : 0,
    refR: hi != null ? pct(hi) : 100,
    optL: oLo != null ? pct(oLo) : (hasRef && lo != null ? pct(lo) : 0),
    optR: oHi != null ? pct(oHi) : 100,
    hasRef, hasOpt,
  };
}

function useFormat() {
  const { language } = useTranslation();
  const locale = language === "de" ? "de-DE" : "en-GB";
  return useMemo(() => {
    const nf = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
    const df = new Intl.DateTimeFormat(locale, { month: "short", year: "2-digit" });
    return {
      num: (v) => (toNum(v) == null ? "—" : nf.format(toNum(v))),
      monthYear: (d) => df.format(new Date(`${d}T00:00:00`)),
    };
  }, [locale]);
}

// "8–12" · "ab 8" · "bis 6"
function rangeText(t, num, lo, hi) {
  const l = toNum(lo), h = toNum(hi);
  if (l != null && h != null) return `${num(l)}–${num(h)}`;
  if (l != null) return `${t("omega_range_from")} ${num(l)}`;
  if (h != null) return `${t("omega_range_upto")} ${num(h)}`;
  return "";
}

// Brand accent (rust) used for "Bei dir" labels, bullets, step numbers
function useAccent() {
  const theme = useTheme();
  return theme.palette.mode === "dark" ? theme.palette.primary.C500 : theme.palette.primary.C800;
}

const reducedMotion = { "@media (prefers-reduced-motion: reduce)": { transition: "none" } };

// ─── Gauge (verbatim pattern from BloodtestsPanel / OvarianDetailsFemale) ─────────

function GaugeBar({ value, low, high, optLow, optHigh }) {
  const theme = useTheme();
  if (value == null || (low == null && high == null)) return null;
  const val = Number(value);
  const lo  = low  != null ? Number(low)  : null;
  const hi  = high != null ? Number(high) : null;
  const toNum = (v) => (v == null || v === "" || isNaN(parseFloat(v)) ? null : parseFloat(v));
  const oLo = toNum(optLow);
  const oHi = toNum(optHigh);
  let dMin, dMax;
  if (lo != null && hi != null) {
    const range = (hi - lo) || 1;
    dMin = lo - range * 0.5;
    dMax = hi + range * 0.5;
  } else if (lo != null) {
    dMin = Math.min(0, lo * 0.5);
    dMax = Math.max(lo * 2, val * 1.2);
  } else {
    dMin = Math.min(0, val * 0.5);
    dMax = Math.max(hi * 1.5, val * 1.2);
  }
  const pad = (dMax - dMin) * 0.05 || 1;
  dMin = Math.min(dMin, val - pad, ...(oLo != null ? [oLo - pad] : []));
  dMax = Math.max(dMax, val + pad, ...(oHi != null ? [oHi + pad] : []));
  const span    = dMax - dMin;
  const clamp   = (v) => Math.max(0, Math.min(100, ((v - dMin) / span) * 100));
  const lowPct  = lo != null ? clamp(lo) : 0;
  const highPct = hi != null ? clamp(hi) : 100;
  const valPct  = clamp(val);
  const isNormal = (lo == null || val >= lo) && (hi == null || val <= hi);
  const optLowPct  = oLo != null ? clamp(oLo) : lowPct;
  const optHighPct = oHi != null ? clamp(oHi) : highPct;
  return (
    <Box sx={{ position: "relative", width: "100%", pb: 1 }}>
      <Box sx={{ position: "relative", width: "100%", height: 5, bgcolor: "action.disabledBackground", borderRadius: 0.5 }}>
        <Box sx={{ position: "absolute", left: `${lowPct}%`, width: `${highPct - lowPct}%`, height: "100%", bgcolor: theme.palette.traffic.green, opacity: 0.4 }} />
        {(oLo != null || oHi != null) && optHighPct > optLowPct && (
          <Box sx={{ position: "absolute", left: `${optLowPct}%`, width: `${optHighPct - optLowPct}%`, height: "100%", bgcolor: theme.palette.mode === "dark" ? theme.palette.quartiary.C300 : theme.palette.quartiary.C700 }} />
        )}
        <Box sx={{ position: "absolute", top: "50%", left: `${valPct}%`, transform: "translate(-50%, -50%)", width: 4, height: 14, bgcolor: isNormal ? theme.palette.traffic.green : theme.palette.traffic.red }} />
      </Box>
      {lo != null && (
        <Typography variant="caption" color="text.disabled" sx={{ position: "absolute", top: "100%", left: `${lowPct}%`, transform: "translateX(-50%)", fontSize: "0.6rem", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {lo.toFixed(2)}
        </Typography>
      )}
      {hi != null && (
        <Typography variant="caption" color="text.disabled" sx={{ position: "absolute", top: "100%", left: `${highPct}%`, transform: "translateX(-50%)", fontSize: "0.6rem", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {hi.toFixed(2)}
        </Typography>
      )}
    </Box>
  );
}

// ─── Range track — reference band, optimal band, value marker ────────────────────

function RangeTrack({ marker, variant = "card" }) {
  const theme = useTheme();
  const g = rangeGeometry(marker);
  if (!g) return null;
  const dark = theme.palette.mode === "dark";
  const green = theme.palette.traffic.green;
  const hero = variant === "hero";
  const out = isOutOfRange(rangeState(marker));
  const height = hero ? 14 : 6;

  return (
    <Box sx={{ position: "relative", height, borderRadius: height, overflow: "visible",
      bgcolor: hero ? alpha("#fff", 0.12) : (dark ? alpha("#fff", 0.1) : "grey.C200") }}>
      {g.hasRef && (
        <Box sx={{
          position: "absolute", top: 0, bottom: 0, left: `${g.refL}%`, width: `${g.refR - g.refL}%`,
          borderRadius: height,
          ...(hero
            ? { bgcolor: alpha("#fff", 0.3) }
            : { bgcolor: alpha(green, 0.16), boxShadow: `inset 0 0 0 1px ${alpha(green, 0.55)}` }),
        }} />
      )}
      {g.hasOpt && g.optR > g.optL && (
        <Box sx={{
          position: "absolute", top: 0, bottom: 0, left: `${g.optL}%`, width: `${g.optR - g.optL}%`,
          borderRadius: height, bgcolor: green,
        }} />
      )}
      {/* Full-width layer moved by transform, so the marker glides between visits */}
      <Box sx={{
        position: "absolute", inset: 0, pointerEvents: "none",
        transform: `translateX(${g.val}%)`,
        transition: `transform 520ms ${EASE_OUT}`,
        ...reducedMotion,
      }}>
        {hero ? (
          <Box sx={{
            position: "absolute", left: 0, top: "50%", transform: "translate(-50%, -50%)",
            width: 4, height: 26, borderRadius: 2, bgcolor: "#fff",
            boxShadow: `0 0 0 2px ${theme.palette.greydark.C900}, 0 2px 6px rgba(0,0,0,0.35)`,
          }} />
        ) : (
          <Box sx={{
            position: "absolute", left: 0, top: "50%", transform: "translate(-50%, -50%)",
            width: 11, height: 11, borderRadius: "50%",
            bgcolor: out ? theme.palette.traffic.red : (dark ? theme.palette.traffic.green : "hsl(124, 66%, 30%)"),
            boxShadow: `0 0 0 2px ${dark ? theme.palette.greydark.C980 : "#fff"}`,
          }} />
        )}
      </Box>
    </Box>
  );
}

// ─── Hero — Omega-3 index, explanation, range, history ──────────────────────────

function OmegaHero({ index3, history, selectedId, onSelectVisit, onInfo, onChart }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { num, monthYear } = useFormat();
  const state = rangeState(index3);
  const g = rangeGeometry(index3);
  const unit = index3.unit?.includes("%") ? "%" : (index3.unit || "");
  const out = isOutOfRange(state);
  const onDark = alpha("#fff", 0.72);

  const labText = rangeText(t, num, index3.ref_low, index3.ref_high);
  const optText = rangeText(t, num, index3.opt_low, index3.opt_high);
  // Lab label ends at the lab band's start; optimal label starts at the optimal band
  const labAnchor = g ? (toNum(index3.ref_low) != null ? g.refL : g.refR) : 0;
  const labRightAligned = labAnchor > 30;
  const optRightAligned = g ? g.optL > 70 : false;

  const iconBtnSx = {
    color: onDark,
    transition: `transform 160ms ${EASE_OUT}, color 150ms ease`,
    "&:active": { transform: "scale(0.92)" },
    "@media (hover: hover) and (pointer: fine)": { "&:hover": { color: "#fff", bgcolor: alpha("#fff", 0.08) } },
  };

  return (
    <Box component="section" aria-labelledby="omega-hero-title" sx={{
      bgcolor: "greydark.C900", color: "#fff", borderRadius: "24px",
      border: theme.palette.mode === "dark" ? `1px solid ${alpha("#fff", 0.08)}` : "none",
      p: { xs: 2.5, sm: 3.5 }, mb: 1.5,
      display: "grid", gap: { xs: 3, md: 5 },
      gridTemplateColumns: { xs: "1fr", md: "minmax(200px, auto) minmax(0, 1fr)" },
    }}>
      <Box>
        <Typography id="omega-hero-title" component="h2" sx={{
          fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
          color: theme.palette.primary.C500,
        }}>
          {t("omega_hero_eyebrow")}
        </Typography>
        <Typography component="p" sx={{ mt: 1, lineHeight: 1, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          <Box component="span" sx={{ fontSize: { xs: "3.6rem", sm: "4.5rem" }, fontWeight: 600, letterSpacing: "-0.035em" }}>
            {num(index3.value)}
          </Box>
          {unit && (
            <Box component="span" sx={{ ml: 0.75, fontSize: { xs: "1.4rem", sm: "1.7rem" }, fontWeight: 500, color: onDark }}>{unit}</Box>
          )}
        </Typography>
        {state && (
          <Box sx={{
            mt: 2, display: "inline-flex", alignItems: "center", gap: 1,
            px: 1.75, py: 0.875, borderRadius: "14px", bgcolor: "#fff", maxWidth: 280,
          }}>
            <Box sx={{ width: 8, height: 8, flexShrink: 0, borderRadius: "50%",
              bgcolor: out ? theme.palette.traffic.red : "hsl(124, 66%, 34%)" }} />
            <Typography sx={{ fontSize: "0.84rem", fontWeight: 600, lineHeight: 1.35, color: "greydark.C900" }}>
              {t(`omega_state_${state}`)}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Typography sx={{ fontSize: { xs: "0.92rem", sm: "0.98rem" }, lineHeight: 1.6, color: alpha("#fff", 0.86), maxWidth: "60ch" }}>
          {t("omega_hero_explainer")}
        </Typography>

        {g && (
          <Box sx={{ mt: 3.5 }}>
            <RangeTrack marker={index3} variant="hero" />
            <Box sx={{ position: "relative", height: 20, mt: 1.25 }}>
              {g.hasRef && labText && (
                <Typography sx={{
                  position: "absolute", top: 0, left: `${labAnchor}%`, whiteSpace: "nowrap",
                  transform: labRightAligned ? "translateX(calc(-100% - 8px))" : "translateX(8px)",
                  fontSize: "0.72rem", color: onDark, fontVariantNumeric: "tabular-nums",
                }}>
                  {t("omega_range_lab")} {labText}
                </Typography>
              )}
              {g.hasOpt && optText && (
                <Typography sx={{
                  position: "absolute", top: 0, whiteSpace: "nowrap",
                  ...(optRightAligned ? { right: `${100 - g.optR}%` } : { left: `${g.optL}%` }),
                  fontSize: "0.72rem", fontWeight: 700, color: "#fff", fontVariantNumeric: "tabular-nums",
                }}>
                  {t("omega_range_optimal")} {optText}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ mt: { xs: 2.5, md: "auto" }, pt: { md: 2.5 }, display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          {history.length > 1 && history.map((h) => {
            const current = h.id === selectedId;
            return (
              <ButtonBase
                key={h.id}
                onClick={() => onSelectVisit(h.id)}
                aria-pressed={current}
                aria-label={`${monthYear(h.date)}: ${num(h.value)} ${unit}`}
                sx={{
                  px: 1.25, py: 0.5, borderRadius: "10px", gap: 0.75,
                  fontVariantNumeric: "tabular-nums", fontSize: "0.78rem", color: "#fff",
                  bgcolor: alpha("#fff", current ? 0.2 : 0.08),
                  boxShadow: current ? `inset 0 0 0 1px ${alpha("#fff", 0.45)}` : "none",
                  transition: `background-color 150ms ease, transform 160ms ${EASE_OUT}`,
                  "&:active": { transform: "scale(0.97)" },
                  "&:focus-visible": { outline: `2px solid ${theme.palette.primary.C500}`, outlineOffset: 2 },
                  "@media (hover: hover) and (pointer: fine)": { "&:hover": { bgcolor: alpha("#fff", current ? 0.22 : 0.14) } },
                  ...reducedMotion,
                }}
              >
                <Box component="span" sx={{ color: onDark }}>{monthYear(h.date)}</Box>
                <Box component="span" sx={{ fontWeight: 700 }}>{num(h.value)} {unit}</Box>
              </ButtonBase>
            );
          })}
          <Box sx={{ ml: "auto", display: "flex" }}>
            <Tooltip title="Info" placement="top">
              <IconButton size="small" aria-label="Info" onClick={onInfo} sx={iconBtnSx}>
                <Info size={18} strokeWidth={2.25} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("bt_chart_history")} placement="top">
              <IconButton size="small" aria-label={t("bt_chart_history")} onClick={onChart} sx={iconBtnSx}>
                <ChartNoAxesCombined size={18} strokeWidth={2.25} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Key marker card — EPA / DHA / ALA / ratio; opens the trend chart ────────────

function KeyMarkerCard({ result, label, onOpen }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { num } = useFormat();
  const dark = theme.palette.mode === "dark";
  const state = rangeState(result);
  const out = isOutOfRange(state);
  // Cards only distinguish in / out of lab range; the hero carries the optimal nuance
  const shortState = state === "below_opt" || state === "above_opt" ? "normal" : state;
  return (
    <ButtonBase
      onClick={onOpen}
      aria-label={`${t(`markername_${result.marker_short}`)} — ${t("bt_chart_history")}`}
      sx={{
        display: "flex", flexDirection: "column", alignItems: "stretch", textAlign: "left",
        minWidth: 0, p: 2, borderRadius: "16px",
        bgcolor: dark ? alpha("#fff", 0.04) : alpha("#fff", 0.75),
        boxShadow: `inset 0 0 0 1px ${dark ? alpha("#fff", 0.09) : alpha(theme.palette.greydark.C900, 0.12)}`,
        transition: `box-shadow 150ms ease, transform 160ms ${EASE_OUT}`,
        "&:active": { transform: "scale(0.98)" },
        "&:focus-visible": { outline: `2px solid ${theme.palette.primary.C700}`, outlineOffset: 2 },
        "@media (hover: hover) and (pointer: fine)": {
          "&:hover": { boxShadow: `inset 0 0 0 1px ${dark ? alpha("#fff", 0.24) : alpha(theme.palette.greydark.C900, 0.32)}` },
        },
        ...reducedMotion,
      }}
    >
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, lineHeight: 1.3 }}>{label}</Typography>
      <Typography sx={{ mt: 0.5, lineHeight: 1.1, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
        <Box component="span" sx={{ fontSize: { xs: "1.45rem", sm: "1.65rem" }, fontWeight: 500, letterSpacing: "-0.02em" }}>
          {num(result.value)}
        </Box>
        {result.unit && (
          <Box component="span" sx={{ ml: 0.5, fontSize: "0.78rem", color: "text.secondary" }}>{result.unit}</Box>
        )}
      </Typography>
      <Typography sx={{
        mt: 0.5, mb: 1.75, fontSize: "0.76rem", lineHeight: 1.4, color: "text.secondary",
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", minHeight: "2.8em",
      }}>
        {t(`omega_card_desc_${result.marker_short}`)}
      </Typography>
      <Box sx={{ mt: "auto", px: 0.5 }}>
        <RangeTrack marker={result} />
      </Box>
      {shortState && (
        <Typography sx={{
          mt: 1.5, fontSize: "0.76rem", fontWeight: 700,
          color: out ? theme.palette.traffic.red : (dark ? theme.palette.traffic.green : "hsl(124, 66%, 30%)"),
        }}>
          {t(`omega_state_${shortState}`)}
        </Typography>
      )}
    </ButtonBase>
  );
}


// ─── Section shell — card with title + either real content or a pending state ────

// One heading style for every section on the page (inside cards and above the marker list)
const sectionHeadingSx = (theme) => ({
  fontSize: { xs: "1.15rem", sm: "1.3rem" }, fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.01em",
  color: theme.palette.mode === "dark" ? "grey.C50" : "greydark.C900",
});

function DetailSection({ title, children, hasContent = true, pendingText, flush = false }) {
  const theme = useTheme();
  return (
    <Box sx={{
      mb: 2,
      overflow: "hidden",
      bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
      backdropFilter: "blur(28px) saturate(180%)",
      WebkitBackdropFilter: "blur(28px) saturate(180%)",
      border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
      boxShadow: theme.palette.mode === "dark"
        ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
        : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
      borderRadius: "24px",
    }}>
      <Typography component="h2" sx={{ ...sectionHeadingSx(theme), px: { xs: 2.25, sm: 3 }, pt: { xs: 2.25, sm: 2.75 }, pb: 1.25 }}>
        {title}
      </Typography>
      {/* flush: content brings its own horizontal padding (accordion rows) */}
      <Box sx={{ px: flush ? 0 : { xs: 2.25, sm: 3 }, pb: flush ? 1 : { xs: 2.25, sm: 3 } }}>
        {hasContent ? children : (
          <Typography sx={{ fontSize: "0.95rem", color: "text.secondary", lineHeight: 1.6 }}>
            {pendingText}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Numbered item — for ordered, enumerable list content inside a card ──────────

function NumberedItem({ number, text, accentColor, isLast }) {
  const theme = useTheme();
  return (
    <>
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 1.5 }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 28, height: 28,
            borderRadius: "50%",
            border: `2px solid ${accentColor}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            mt: "2px",
          }}
        >
          <Typography
            variant="number4"
            sx={{ color: accentColor, fontWeight: 700, fontSize: "0.75rem", lineHeight: 1 }}
          >
            {number}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: { xs: "0.95rem", sm: "1rem" }, color: theme.palette.mode === "dark" ? theme.palette.grey.C300 : theme.palette.greydark.C700, lineHeight: 1.7 }}>
          {text}
        </Typography>
      </Stack>
      {!isLast && (
        <Divider sx={{ borderColor: alpha(theme.palette.mode === "dark" ? "#fff" : theme.palette.greydark.C900, 0.08) }} />
      )}
    </>
  );
}

// ─── Insight report — normalise new `sections` and legacy flat fields ────────────

// Returns null when the report carries no displayable content at all
function normalizeInsights(d) {
  if (!d) return null;
  const s = d.sections && Object.keys(d.sections).length ? d.sections : null;
  const topic = (key) => {
    const sec = s?.[key];
    const text = sec?.text || d[key] || "";
    const points = Array.isArray(sec?.points) ? sec.points.filter(Boolean) : [];
    if (!text && !points.length) return null;
    return { title: sec?.title || null, text, points };
  };

  // Prefer `steps` (action + reason split); fall back to `actions[].text`, then the flat field.
  // Both describe the same measures, so only one source is ever rendered.
  const pa = s?.personalized_actions;
  let steps = [];
  if (Array.isArray(pa?.steps) && pa.steps.length) {
    steps = pa.steps.map((st) => ({ headline: st.action, reason: st.reason || null, frequency: st.frequency || null }));
  } else if (Array.isArray(pa?.actions) && pa.actions.length) {
    steps = pa.actions.map((a) => ({ headline: a.text, reason: null, frequency: a.frequency || null }));
  } else if (d.personalized_actions) {
    steps = d.personalized_actions.split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean)
      .map((text) => ({ headline: text, reason: null, frequency: null }));
  }
  steps = steps.filter((st) => st.headline);

  const result = {
    keyInsights: topic("key_insights"),
    functions: Object.fromEntries(FUNCTION_TILES.map((f) => [f.field, topic(f.field)])),
    actions: steps.length || pa?.text ? { title: pa?.title || null, intro: pa?.text || "", steps } : null,
    safety: s?.safety_notice?.text ? { title: s.safety_notice.title || null, text: s.safety_notice.text } : null,
  };
  const hasAny = result.keyInsights || result.actions || result.safety || Object.values(result.functions).some(Boolean);
  return hasAny ? result : null;
}

const bodyTextSx = (theme) => ({
  fontSize: { xs: "0.95rem", sm: "0.98rem" }, lineHeight: 1.7, maxWidth: "68ch",
  color: theme.palette.mode === "dark" ? theme.palette.grey.C300 : theme.palette.greydark.C700,
  whiteSpace: "pre-line",
});

// Small uppercase label in the brand accent, set inline before a heading (per design reference)
function Eyebrow({ children, sx }) {
  const accent = useAccent();
  return (
    <Typography component="span" sx={{
      fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
      color: accent, lineHeight: 1.4, ...sx,
    }}>
      {children}
    </Typography>
  );
}

function SectionHeading({ eyebrow, title, id }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.25, mb: 2 }}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <Typography id={id} component="h2" sx={sectionHeadingSx(theme)}>{title}</Typography>
    </Box>
  );
}

// "Bei dir" aside — the personal bullet points next to each narrative
function PersonalPoints({ points, tone = "paper" }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const accent = useAccent();
  const dark = theme.palette.mode === "dark";
  if (!points?.length) return null;
  return (
    <Box component="aside" sx={{
      alignSelf: "start", p: { xs: 2, sm: 2.25 }, borderRadius: "16px",
      bgcolor: tone === "tint"
        ? alpha(theme.palette.primary.C700, dark ? 0.12 : 0.08)
        : (dark ? alpha("#fff", 0.05) : "#fff"),
    }}>
      <Eyebrow sx={{ display: "block", mb: 1.25 }}>{t("omega_bei_dir")}</Eyebrow>
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 1.25 }}>
        {points.map((p, i) => (
          <Box component="li" key={i} sx={{ display: "flex", gap: 1.25 }}>
            <Box aria-hidden sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: accent, flexShrink: 0, mt: "0.55em" }} />
            <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.55, color: dark ? "grey.C200" : "greydark.C800" }}>{p}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// Narrative paragraph + optional "Bei dir" aside, side by side from md up
function NarrativeWithPoints({ text, points, tone }) {
  const theme = useTheme();
  const hasPoints = points?.length > 0;
  return (
    <Box sx={{
      display: "grid", gap: { xs: 2, md: 3.5 },
      gridTemplateColumns: { xs: "1fr", md: hasPoints ? "minmax(0, 1.5fr) minmax(0, 1fr)" : "1fr" },
    }}>
      {text && <Typography sx={bodyTextSx(theme)}>{text}</Typography>}
      {hasPoints && <PersonalPoints points={points} tone={tone} />}
    </Box>
  );
}

// One body-function panel: icon + title, then narrative and "Bei dir"
function FunctionSection({ icon: Icon, title, topic, cardStyles }) {
  const theme = useTheme();
  const accent = useAccent();
  const dark = theme.palette.mode === "dark";
  return (
    <Box component="article" sx={{ ...cardStyles, p: { xs: 2.25, sm: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: dark ? alpha("#fff", 0.08) : "#fff",
        }}>
          {Icon && <Icon size={18} strokeWidth={2.25} color={accent} aria-hidden />}
        </Box>
        <Typography component="h3" sx={{ fontSize: { xs: "1.05rem", sm: "1.12rem" }, fontWeight: 700, lineHeight: 1.3 }}>
          {title}
        </Typography>
      </Box>
      <NarrativeWithPoints text={topic.text} points={topic.points} />
    </Box>
  );
}

// Numbered, prioritised next steps with a frequency badge
function ActionSteps({ steps }) {
  const theme = useTheme();
  const accent = useAccent();
  const dark = theme.palette.mode === "dark";
  const rule = alpha(dark ? "#fff" : theme.palette.greydark.C900, dark ? 0.12 : 0.14);
  return (
    <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none", borderTop: `2px solid ${rule}` }}>
      {steps.map((st, i) => (
        <Box component="li" key={i} sx={{
          display: "flex", gap: { xs: 1.5, sm: 2 }, py: 2,
          borderBottom: i < steps.length - 1 ? `1px solid ${rule}` : "none",
        }}>
          <Box aria-hidden sx={{
            width: 28, height: 28, borderRadius: "50%", flexShrink: 0, mt: "1px",
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: accent, color: dark ? "greydark.C900" : "#fff",
          }}>
            <Typography variant="number4" sx={{ fontSize: "0.8rem", fontWeight: 700, lineHeight: 1, color: "inherit" }}>{i + 1}</Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{
              fontSize: { xs: "0.95rem", sm: "1rem" }, lineHeight: 1.6, maxWidth: "70ch",
              // Headline only reads as one when a separate reason follows; a merged paragraph stays body weight
              fontWeight: st.reason ? 650 : 400,
              color: st.reason ? "text.primary" : bodyTextSx(theme).color,
            }}>
              {st.headline}
              {st.frequency && (
                <Box component="span" sx={{
                  display: "inline-block", ml: 1, px: 1, py: "1px", borderRadius: "999px",
                  fontSize: "0.72rem", fontWeight: 600, lineHeight: 1.6, verticalAlign: "0.1em", whiteSpace: "nowrap",
                  color: accent, bgcolor: alpha(theme.palette.primary.C700, dark ? 0.16 : 0.1),
                }}>
                  {st.frequency}
                </Box>
              )}
            </Typography>
            {st.reason && (
              <Typography sx={{ mt: 0.5, fontSize: "0.9rem", lineHeight: 1.6, color: "text.secondary", maxWidth: "70ch" }}>
                {st.reason}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function SafetyNotice({ title, text }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  return (
    <Box role="note" aria-label={title || undefined} sx={{
      mt: 3, p: { xs: 2.25, sm: 2.75 }, borderRadius: "16px",
      bgcolor: alpha(theme.palette.primary.C600, dark ? 0.16 : 0.2),
    }}>
      {title && (
        <Typography component="h3" sx={{ fontSize: "0.95rem", fontWeight: 700, mb: 0.5 }}>{title}</Typography>
      )}
      <Typography sx={{ fontSize: "0.92rem", lineHeight: 1.65, maxWidth: "75ch", color: dark ? "grey.C200" : "greydark.C800" }}>
        {text}
      </Typography>
    </Box>
  );
}

function InsightsPlaceholder({ status }) {
  const theme = useTheme();
  const { t } = useTranslation();
  if (status === "loading") {
    return (
      <Box aria-busy="true" aria-label={t("omega_report_loading")} sx={{ py: 1 }}>
        <Skeleton variant="text" width="40%" height={32} />
        {[100, 96, 92, 70].map((w, i) => <Skeleton key={i} variant="text" width={`${w}%`} />)}
      </Box>
    );
  }
  const error = status === "error";
  return (
    <Box sx={{
      display: "flex", gap: 2, alignItems: "flex-start",
      p: { xs: 2.25, sm: 3 }, borderRadius: "20px", border: "1px dashed",
      borderColor: theme.palette.mode === "dark" ? "greydark.C400" : "grey.C900",
    }}>
      <Box sx={{ color: "text.secondary", mt: "2px", flexShrink: 0 }}><FileClock size={22} aria-hidden /></Box>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.98rem" }}>
          {t(error ? "omega_report_error_title" : "omega_report_pending_title")}
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: "0.92rem", lineHeight: 1.6, color: "text.secondary", maxWidth: "62ch" }}>
          {t(error ? "omega_report_error" : "omega_key_insights_pending")}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function OmegaDetails() {

  const theme = useTheme();
  const { t } = useTranslation();

  const cardStyles = useMemo(() => ({
    bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
    backdropFilter: "blur(28px) saturate(180%)",
    WebkitBackdropFilter: "blur(28px) saturate(180%)",
    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
    boxShadow: theme.palette.mode === "dark"
      ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
      : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
    borderRadius: "24px",
  }), [theme.palette.mode]);

  const [visitOptions, setVisitOptions]   = useState([]);
  const [selectedId, setSelectedId]       = useState(null);
  const [reportError, setReportError]     = useState(false);
  const [datesLoading, setDatesLoading]   = useState(true);

  const [infoOpen, setInfoOpen]     = useState(false);
  const [infoMarker, setInfoMarker] = useState(null);
  const [chartOpen, setChartOpen]     = useState(false);
  const [chartMarker, setChartMarker] = useState(null);
  const [toggledSubs, setToggledSubs] = useState(() => new Set());

  // status: idle | loading | ready | empty | error
  const [insights, setInsights] = useState({ status: "idle", data: null });

  // Reports are imported by staff, so a 404 (no OmegaInsight for this lab visit yet)
  // is an expected empty state, not an error. 403 and anything else is an error.
  useEffect(() => {
    if (!selectedId) { setInsights({ status: "idle", data: null }); return; }
    let cancelled = false;
    setInsights({ status: "loading", data: null });
    axios.get(`${API_URL}/api/omega/${selectedId}/insights/`)
      .then(({ data }) => {
        if (cancelled) return;
        const normalized = normalizeInsights(data);
        setInsights(normalized ? { status: "ready", data: normalized } : { status: "empty", data: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setInsights({ status: err?.response?.status === 404 ? "empty" : "error", data: null });
      });
    return () => { cancelled = true; };
  }, [selectedId]);

  useEffect(() => {
    const fetchVisits = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/all-lab-visits/results/`);
        const raw = res.data;
        const labVisits = Array.isArray(raw) ? raw : (raw.lab_visits || []);
        const completed = labVisits.filter((v) => Number(v.state) === 6).sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date));
        setVisitOptions(completed);
        if (completed.length > 0) setSelectedId((prev) => prev ?? completed[0].id);
      } catch (err) {
        console.error("Failed to fetch lab visits", err);
        setReportError("fetch");
      } finally {
        setDatesLoading(false);
      }
    };
    fetchVisits();
  }, []);

  useEffect(() => {
    if (!selectedId || !visitOptions.length) return;
    const v = visitOptions.find((v) => v.id === selectedId);
    setReportError(v?.results?.length ? false : "empty");
  }, [selectedId, visitOptions]);

  const report = visitOptions.find((v) => v.id === selectedId) ?? null;

  const omegaResults = useMemo(
    () => (report?.results ?? []).filter(r => r.marker_category === "Omega Health"),
    [selectedId, visitOptions] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const groupedBySubsystem = useMemo(() => {
    const map = {};
    for (const m of omegaResults) {
      const key = m.marker_healthsubsystem?.trim() ?? "__none__";
      if (!map[key]) map[key] = [];
      map[key].push(m);
    }
    Object.values(map).forEach(g => g.sort((a, b) => (a.marker_order ?? 0) - (b.marker_order ?? 0)));
    return map;
  }, [omegaResults]);

  const subsystemKeys = Object.keys(groupedBySubsystem).sort((a, b) => {
    const posA = OMEGA_SUBSYSTEM_ORDER.indexOf(a);
    const posB = OMEGA_SUBSYSTEM_ORDER.indexOf(b);
    if (posA >= 0 && posB >= 0) return posA - posB;
    if (posA >= 0) return -1;
    if (posB >= 0) return 1;
    return a.localeCompare(b);
  });

  const index3Result = omegaResults.find(r => r.marker_short === "INDEX3");
  const keyCards = KEY_CARDS
    .map((c) => ({ ...c, result: omegaResults.find((r) => r.marker_short === c.code) }))
    .filter((c) => c.result);

  // Omega-3 index across visits, oldest → newest, last four
  const index3History = useMemo(() => visitOptions
    .map((v) => ({ id: v.id, date: v.visit_date, value: v.results?.find((r) => r.marker_short === "INDEX3")?.value }))
    .filter((h) => toNum(h.value) != null)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(-4), [visitOptions]);

  const openChart = (code) => { setChartMarker(code); setChartOpen(true); };
  const ins = insights.data;

  const hasResults = report && !reportError;
  // Quiet icon buttons in marker rows — press feedback instead of growing on hover
  const rowIconBtnSx = {
    color: "text.secondary",
    transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
    "&:active": { transform: "scale(0.92)" },
  };

  if (datesLoading) {
    return (
      <Backdrop open sx={{ zIndex: t => t.zIndex.drawer + 1 }}>
        <CircularProgress />
      </Backdrop>
    );
  }

  return (

    <PageProtectedLayout

      headline={t('page_hl_omega')}
      specify={t('page_sp_omega_lifestyle')}

      >

      <Box sx={{ width: "100%", maxWidth: 800, mt: 1, mb: 10, px: { xs: 2, sm: 4, md: 6 } }}>

        {visitOptions.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: "center", mt: 6 }}>{t("bt_no_visits")}</Typography>
        ) : (
            <>
              {/* ── Visit date ───────────────────────────────────────────────── */}
              <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
                <DateSelector
                  dates={visitOptions.map((v) => v.visit_date)}
                  selectedDate={report?.visit_date}
                  onSelect={(date) => setSelectedId(visitOptions.find((v) => v.visit_date === date)?.id ?? selectedId)}
                />
              </Box>

              {reportError ? (
                <Box sx={{ ...cardStyles, p: { xs: 2.25, sm: 3 } }}>
                  <Typography color="text.secondary">
                    {reportError === "empty" ? t("bt_no_results_yet") : t("bt_results_error")}
                  </Typography>
                </Box>
              ) : hasResults && omegaResults.length === 0 ? (
                <Box sx={{ p: 2.5, borderRadius: "18px", border: "1px dashed", borderColor: "divider", textAlign: "center" }}>
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.secondary" }}>
                    {t("bt_not_ordered")}
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", mt: 0.5 }}>
                    {t("bt_consider_addon")}
                  </Typography>
                </Box>
              ) : null}

              {hasResults && omegaResults.length > 0 && (
                <>
                  {/* ── Hero: Omega-3 index ─────────────────────────────────── */}
                  {index3Result && (
                    <OmegaHero
                      index3={index3Result}
                      history={index3History}
                      selectedId={selectedId}
                      onSelectVisit={setSelectedId}
                      onInfo={() => { setInfoMarker("INDEX3"); setInfoOpen(true); }}
                      onChart={() => openChart("INDEX3")}
                    />
                  )}

                  {/* ── Key marker cards + range legend ─────────────────────── */}
                  {keyCards.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{
                        display: "grid", gap: 1.5,
                        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: `repeat(${Math.min(keyCards.length, 4)}, minmax(0, 1fr))` },
                      }}>
                        {keyCards.map((c) => (
                          <KeyMarkerCard key={c.code} result={c.result} label={c.label} onOpen={() => openChart(c.code)} />
                        ))}
                      </Box>
                      <Box sx={{ display: "flex", columnGap: 2, mt: 1.25, px: 0.5 }}>
                        {[
                          { label: t("omega_legend_ref"), sx: { bgcolor: alpha(theme.palette.traffic.green, 0.16), boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.traffic.green, 0.55)}` } },
                          { label: t("omega_legend_opt"), sx: { bgcolor: theme.palette.traffic.green } },
                        ].map(({ label, sx }) => (
                          <Box key={label} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                            <Box sx={{ width: 18, height: 7, borderRadius: 4, ...sx }} />
                            <Typography sx={{ fontSize: "0.74rem", color: "text.secondary", lineHeight: 1.4 }}>{label}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* ── Fatty Acid Composition ──────────────────────────────── */}
                  <DetailSection title={t("bt_omega_chart_section_title")}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                    <Box sx={{ flex: { xs: "0 0 auto", sm: "1 1 320px" }, minWidth: 0, width: "100%" }}>
                      <OmegaCompositionChart results={report.results} theme={theme} t={t} />
                    </Box>
                    <Stack spacing={1} sx={{ flex: { xs: "0 0 auto", sm: "1 1 220px" }, px: 1 }}>
                      {["bt_omega_abbrev_pufa", "bt_omega_abbrev_mufa", "bt_omega_abbrev_sfa"].map((key) => (
                        <Typography key={key} sx={{ fontSize: "0.82rem", color: "text.secondary", lineHeight: 1.5 }}>{t(key)}</Typography>
                      ))}
                    </Stack>
                  </Stack>
                  </DetailSection>

                  {/* ── Personal report (GET /api/omega/{labVisitId}/insights/) ── */}
                  {insights.status !== "ready" ? (
                    <Box sx={{ mt: 5, mb: 2 }}>
                      <InsightsPlaceholder status={insights.status} />
                    </Box>
                  ) : (
                    <Box sx={{
                      // Content arrives after the lab data — fade it in instead of popping
                      animation: "omegaReportIn 320ms ease-out both",
                      "@keyframes omegaReportIn": { from: { opacity: 0 }, to: { opacity: 1 } },
                    }}>
                      {ins.keyInsights && (
                        <Box component="section" aria-labelledby="omega-key-insights" sx={{ mt: 5, mb: 6 }}>
                          <SectionHeading id="omega-key-insights" title={ins.keyInsights.title || t("omega_key_insights_title")} />
                          <NarrativeWithPoints text={ins.keyInsights.text} points={ins.keyInsights.points} tone="tint" />
                        </Box>
                      )}

                      {FUNCTION_TILES.some((f) => ins.functions[f.field]) && (
                        <Box component="section" aria-labelledby="omega-body" sx={{ mb: 6 }}>
                          <SectionHeading id="omega-body" eyebrow={t("omega_eyebrow_body")} title={t("omega_function_title")} />
                          <Stack spacing={2}>
                            {FUNCTION_TILES.filter((f) => ins.functions[f.field]).map((f) => (
                              <FunctionSection
                                key={f.key}
                                icon={f.icon}
                                title={ins.functions[f.field].title || t(f.titleKey)}
                                topic={ins.functions[f.field]}
                                cardStyles={cardStyles}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {(ins.actions || ins.safety) && (
                        <Box component="section" aria-labelledby="omega-actions" sx={{ mb: 6 }}>
                          <SectionHeading
                            id="omega-actions"
                            eyebrow={t("omega_eyebrow_steps")}
                            title={ins.actions?.title || t("omega_actions_title")}
                          />
                          {ins.actions?.intro && (
                            <Typography sx={{ ...bodyTextSx(theme), mb: 2.5 }}>{ins.actions.intro}</Typography>
                          )}
                          {ins.actions?.steps.length > 0 && <ActionSteps steps={ins.actions.steps} />}
                          {ins.safety && <SafetyNotice title={ins.safety.title} text={ins.safety.text} />}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* ── Guidelines (static, non-personalized) ──────────────── */}
                  <DetailSection title={t("bt_omega_guidelines_title")}>
                  <Box>
                    {["bt_omega_guideline_1", "bt_omega_guideline_2", "bt_omega_guideline_3", "bt_omega_guideline_4"].map((key, idx, arr) => (
                      <NumberedItem
                        key={key}
                        number={idx + 1}
                        text={t(key)}
                        accentColor={theme.palette.tertiary.C300}
                        isLast={idx === arr.length - 1}
                      />
                    ))}
                  </Box>
                  </DetailSection>

                  {/* ── Full marker list, grouped by subsystem ─────────────── */}
                  <Typography component="h2" sx={{ ...sectionHeadingSx(theme), mt: 4, mb: 1.5, px: 0.5 }}>
                    {t("omega_markers_title")}
                  </Typography>
                  {subsystemKeys.map((sk, subIdx) => {
                    const markers     = groupedBySubsystem[sk];
                    const subLabelKey = SUBSYSTEM_KEY[sk];
                    const subLabel    = sk === "__none__" ? null : (subLabelKey ? t(subLabelKey) : sk);
                    const redCount    = markers.filter(m => m.status && m.status.toUpperCase() !== "NORMAL").length;
                    const dark        = theme.palette.mode === "dark";
                    const defaultSubOpen = subIdx === 0;
                    const subOpen = toggledSubs.has(sk) ? !defaultSubOpen : defaultSubOpen;
                    const toggleSub = () => {
                      setToggledSubs((prev) => {
                        const next = new Set(prev);
                        if (next.has(sk)) next.delete(sk); else next.add(sk);
                        return next;
                      });
                    };
                    return (
                      <Box key={sk} sx={{ mb: 1.5 }}>
                        <Accordion
                          expanded={subOpen}
                          onChange={toggleSub}
                          disableGutters
                          elevation={0}
                          slotProps={{ transition: { timeout: 320 } }}
                          sx={{
                            borderRadius: "18px !important",
                            overflow: "hidden",
                            "&:before": { display: "none" },
                            bgcolor: dark ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
                            backdropFilter: "blur(28px) saturate(180%)",
                            WebkitBackdropFilter: "blur(28px) saturate(180%)",
                            border: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.35)"}`,
                            boxShadow: dark
                              ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
                              : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={subOpen
                              ? <Minus size={18} color={theme.palette.mode === "light" ? theme.palette.greydark.C900 : theme.palette.grey.C50} />
                              : <Plus size={18} color={theme.palette.mode === "light" ? theme.palette.greydark.C900 : theme.palette.grey.C50} />}
                            sx={{
                              px: { xs: 2.25, sm: 2.5 },
                              bgcolor: "transparent",
                              minHeight: 56,
                              "& .MuiAccordionSummary-content": { my: 1, display: "flex", alignItems: "center", gap: 1, pr: 2 },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              {subLabel && (
                                <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.3 }}>
                                  {subLabel}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ flexGrow: 1 }} />
                            {redCount > 0 ? (
                              <Tooltip title={t("bt_tooltip_out_of_range")} placement="top">
                                <Chip
                                  label={`${redCount}`}
                                  size="small"
                                  sx={{
                                    bgcolor: "transparent",
                                    color: theme.palette.traffic.red,
                                    border: "1px solid", borderColor: theme.palette.traffic.red,
                                    fontWeight: 500, fontSize: "0.8rem",
                                    width: 22, height: 22,
                                    borderRadius: "999px",
                                    "& .MuiChip-label": { px: 0, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" },
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip title={t("bt_tooltip_all_in_range")} placement="top">
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <Check size={18} strokeWidth={2.5} color={theme.palette.traffic.green} />
                                </Box>
                              </Tooltip>
                            )}
                          </AccordionSummary>

                          <AccordionDetails sx={{ p: 0 }}>
                            {markers.map((m, i) => {
                              const statusText = statusLabel(t, m.status);
                              return (
                                <Box key={m.id}>
                                  {i > 0 && <Divider sx={{ mx: { xs: 2.25, sm: 2.5 }, borderColor: alpha(dark ? "#fff" : theme.palette.greydark.C900, 0.08) }} />}
                                  <Box sx={{ display: "flex", alignItems: "center", px: { xs: 2.25, sm: 2.5 }, py: 1.25, columnGap: 2, rowGap: { xs: 0.5, sm: 2 }, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flexGrow: 1, minWidth: 120 }}>
                                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: statusColor(theme, m.status), mt: "7px" }} />
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, lineHeight: 1.35 }}>{t(`markername_${m.marker_short}`)}</Typography>
                                        <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", lineHeight: 1.45 }}>{t(`markerdescr_${m.marker_short}`)}</Typography>
                                      </Box>
                                    </Box>
                                    {/* Mobile-only line break: forces the value+gauge pair onto their own row */}
                                    <Box sx={{ flexBasis: "100%", height: 0, display: { xs: "block", sm: "none" } }} />
                                    <Typography variant="number3" sx={{ fontWeight: 600, minWidth: { xs: "auto", sm: 80 }, ml: { xs: "26px", sm: 0 }, textAlign: { xs: "left", sm: "right" }, fontVariantNumeric: "tabular-nums" }}>
                                      {m.value != null ? Number(m.value).toFixed(2) : "—"}{m.unit ? ` ${m.unit}` : ""}
                                    </Typography>
                                    <Box sx={{ minWidth: 140, ml: 2, display: "flex", alignItems: "center" }}>
                                      <GaugeBar value={m.value} low={m.ref_low} high={m.ref_high} optLow={m.opt_low} optHigh={m.opt_high} />
                                    </Box>
                                    {/* Mobile-only line break: forces the check/info/chart icons onto their own row */}
                                    <Box sx={{ flexBasis: "100%", height: 0, display: { xs: "block", sm: "none" } }} />
                                    <Box sx={{ minWidth: { xs: "auto", sm: 68 }, ml: { xs: "26px", sm: 0 }, display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" }, flexShrink: 0 }}>
                                      {m.status?.toUpperCase() === "NORMAL"
                                        ? <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                                        : <Typography variant="subtitle2" fontWeight={700} sx={{ color: statusColor(theme, m.status) }}>{statusText}</Typography>
                                      }
                                    </Box>
                                    <Tooltip title="Info" placement="top">
                                      <IconButton size="small" aria-label="Info" onClick={() => { setInfoMarker(m.marker_short); setInfoOpen(true); }} sx={rowIconBtnSx}>
                                        <Info size={18} strokeWidth={2.25} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t("bt_chart_history")} placement="top">
                                      <IconButton size="small" aria-label={t("bt_chart_history")} onClick={() => { setChartMarker(m.marker_short); setChartOpen(true); }} sx={rowIconBtnSx}>
                                        <ChartNoAxesCombined size={18} strokeWidth={2.25} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              );
                            })}
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    );
                  })}
                </>
              )}
            </>
        )}

      </Box>

      {/* ── Chart history dialog ───────────────────────────────────────────── */}
      <CustomDialog
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        title={chartMarker ? `${t(`markername_${chartMarker}`)} — ${t("bt_chart_title")}` : ""}
        content={chartMarker && <MarkerTrendChart markerShort={chartMarker} visitOptions={visitOptions} theme={theme} t={t} />}
      />

      {/* ── Info dialog ─────────────────────────────────────────────────────── */}
      <CustomDialogSmall
        open={infoOpen}
        onClose={() => { setInfoOpen(false); setInfoMarker(null); }}
        title="Information"
        content={infoMarker && (
          <TextInfoComp
            description={`description_${infoMarker}`}
            lowtext={`lowtext_${infoMarker}`}
            lowrec={`lowrec_${infoMarker}`}
            rangetext={`rangetext_${infoMarker}`}
            hightext={`hightext_${infoMarker}`}
            highrec={`highrec_${infoMarker}`}
          />
        )}
      />

    </PageProtectedLayout>

  );
}
